import { isNativePlatform } from "Utils/Platform/PlatformUtils";

const RETURN_TO_APP_PARAM = "returnToApp";

/**
 * Bounces an external bank-payment return back into the native app shell (iOS or the
 * Capacitor Android app).
 *
 * When a payment is initiated from the app, the bank receives an HTTPS return URL tagged with
 * `?returnToApp=1`. The bank's confirmation page runs in the shell's external browser surface
 * (iOS: the in-app Safari sheet, SFSafariViewController; Android: a Chrome Custom Tab), which
 * is a plain browser context with no Capacitor bridge — so `isNativePlatform()` is `false`
 * here. We redirect to the `moone://` custom scheme, which the shell catches (iOS: AppDelegate;
 * Android: the `moone` intent filter on MainActivity) to dismiss the browser surface and
 * resume the flow in the app's WebView. The path is emitted without a leading slash
 * (`moone://transaction-detail/abc?x=1`); both shells map it onto the app host.
 *
 * Inside the real app WebView `isNativePlatform()` is `true`, so this never self-triggers, and
 * plain-web users never carry the flag — so it is a no-op for them.
 *
 * NOTE: this guard must stay `!isNativePlatform()`, not a platform check. The browser surface
 * has no Capacitor bridge, so `getPlatform()` reports "web" here and `isIOS()` / `isAndroid()`
 * would be `false` — the bounce would never fire. (The decision to *tag* a payment lives in the
 * pay mutations, which run inside the app WebView where the platform helpers are correct.)
 *
 * Must be called once, synchronously, as early as possible (before the router renders) to avoid
 * flashing the landing page inside the browser surface.
 */
export const bounceToNativeAppIfNeeded = (): void => {
  const url = new URL(window.location.href);

  if (!url.searchParams.has(RETURN_TO_APP_PARAM) || isNativePlatform()) {
    return;
  }

  url.searchParams.delete(RETURN_TO_APP_PARAM);
  const path = `${url.pathname}${url.search}${url.hash}`.replace(/^\/+/, "");
  window.location.href = `moone://${path}`;
};
